import React, { useEffect, useRef, useState } from 'react';
import { ChevronLeft, ChevronRight } from 'lucide-react';
import Hero from '../components/Hero';
import { AboutSection, ReferralSection, WhyChoose, Testimonials, CTABand, HomeSpecialOffer, IncomeTaxHighlightSection } from '../components/Sections';
import ServicesForm from './ServicesForm';

const MOBILE_SLIDE_COUNT = 4;
const SWIPE_THRESHOLD = 50;

const Home = () => {
  const [slideIndex, setSlideIndex] = useState(0);
  const touchStartXRef = useRef(null);
  const isTouchingRef = useRef(false);

  const goNextSlide = () => {
    setSlideIndex((prev) => (prev + 1) % MOBILE_SLIDE_COUNT);
  };

  const goPrevSlide = () => {
    setSlideIndex((prev) => (prev - 1 + MOBILE_SLIDE_COUNT) % MOBILE_SLIDE_COUNT);
  };

  useEffect(() => {
    const timer = setInterval(() => {
      if (isTouchingRef.current) return;
      goNextSlide();
    }, 5000);
    return () => clearInterval(timer);
  }, []);

  const onTouchStart = (e) => {
    isTouchingRef.current = true;
    touchStartXRef.current = e.touches?.[0]?.clientX ?? null;
  };

  const onTouchEnd = (e) => {
    if (touchStartXRef.current == null) {
      isTouchingRef.current = false;
      return;
    }
    const touchEndX = e.changedTouches?.[0]?.clientX ?? null;
    if (touchEndX == null) {
      touchStartXRef.current = null;
      return;
    }
    const delta = touchEndX - touchStartXRef.current;
    if (Math.abs(delta) >= SWIPE_THRESHOLD) {
      if (delta < 0) goNextSlide();
      if (delta > 0) goPrevSlide();
    }
    isTouchingRef.current = false;
    touchStartXRef.current = null;
  };

  const onTouchCancel = () => {
    isTouchingRef.current = false;
    touchStartXRef.current = null;
  };

  return (
    <div>
      <HomeSpecialOffer />
      <IncomeTaxHighlightSection />
      <ServicesForm embedded />

      {/* Show top content as auto slides on both mobile and desktop. */}
      <div onTouchStart={onTouchStart} onTouchEnd={onTouchEnd} onTouchCancel={onTouchCancel}>
        {slideIndex === 0 && <Hero />}
        {slideIndex === 1 && <AboutSection />}
        {slideIndex === 2 && <ReferralSection />}
        {slideIndex === 3 && <WhyChoose />}
        <div className="px-4 pb-2">
          <div className="mx-auto w-full max-w-xs lg:max-w-sm rounded-2xl border border-[#14A098]/20 bg-teal-50/70 px-3 py-2">
            <div className="text-[11px] text-center font-medium text-[#0f2540] lg:hidden">Swipe left or right to view sections</div>
            <div className="text-[11px] text-center font-medium text-[#0f2540] hidden lg:block">Use arrows to view sections</div>
            <div className="mt-2 flex items-center justify-center gap-2">
              <button
                type="button"
                onClick={goPrevSlide}
                className="hidden lg:inline-flex items-center justify-center w-6 h-6 rounded-full border border-[#14A098]/30 text-[#0f2540] hover:bg-[#14A098]/10"
                aria-label="Previous section"
              >
                <ChevronLeft className="w-3.5 h-3.5" />
              </button>
              {Array.from({ length: MOBILE_SLIDE_COUNT }, (_, i) => (
                <span
                  key={`mobile-slide-dot-${i + 1}`}
                  className={`h-1.5 rounded-full transition-all ${slideIndex === i ? 'w-5 bg-[#14A098]' : 'w-2 bg-[#14A098]/35'}`}
                />
              ))}
              <button
                type="button"
                onClick={goNextSlide}
                className="hidden lg:inline-flex items-center justify-center w-6 h-6 rounded-full border border-[#14A098]/30 text-[#0f2540] hover:bg-[#14A098]/10"
                aria-label="Next section"
              >
                <ChevronRight className="w-3.5 h-3.5" />
              </button>
            </div>
          </div>
        </div>
      </div>

      {/* Keep this part unchanged as requested. */}
      <Testimonials />
      <CTABand />
    </div>
  );
};

export default Home;
